import React, { useState, useCallback, useEffect, useRef } from 'react'
import { WizardState } from '../Index'
import { buildDefaultTestTitle, hasDuplicateExamWords } from '../titleHelper'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Rocket, CheckCircle2, AlertCircle, FileText,
  BarChart3, ShieldCheck, AlertTriangle, Coins, Clock, Scale, BookOpen, Layers,
  Eye, Edit3, Check, Calculator, Save, Sparkles, Loader2, X, RefreshCw
} from 'lucide-react'
import { KatexRenderer } from '@/components/shared/KatexRenderer'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
  exams?: any[]
  onGenerate: () => void
  generating: boolean
  error: string | null
}

export default function StepReviewGenerate({ state, updateState, exams = [], onGenerate, generating, error }: Props) {
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false)
  const [isEditingModal, setIsEditingModal] = useState(false)
  const [modalInstructions, setModalInstructions] = useState('')

  // ── Title Suggestion state ──────────────────────────────────────────
  const [titleSuggestions, setTitleSuggestions] = useState<string[]>([])
  const [suggestingTitle, setSuggestingTitle] = useState(false)
  const [suggestError, setSuggestError] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Close suggestions popover on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSuggestions])

  const selectedExam = exams.find(e => e.id === state.exam_id)
  const matchedExamTestType = selectedExam?.test_types?.find((tt: any) =>
    (state.exam_test_type_id && tt.id === state.exam_test_type_id) || tt.test_type_key === state.testType
  )

  const initializedRef = useRef(false)
  const userClearedRef = useRef(false)

  // Auto-fill or sanitize default title on mount if blank or contains duplicated exam phrases
  useEffect(() => {
    if (userClearedRef.current) return

    const isDuplicate = hasDuplicateExamWords(state.title, selectedExam?.name)
    if ((!state.title && !initializedRef.current) || isDuplicate) {
      initializedRef.current = true
      const cleanTitle = buildDefaultTestTitle({
        examName: selectedExam?.name,
        examCode: selectedExam?.code,
        testTypeName: matchedExamTestType?.name,
        testTypeKey: state.testType,
        syllabusName: state.syllabiData?.[0]?.name
      })
      if (cleanTitle && cleanTitle !== state.title) {
        updateState({ title: cleanTitle })
      }
    } else {
      initializedRef.current = true
    }
  }, [selectedExam?.name, selectedExam?.code, matchedExamTestType?.name, state.testType])

  const fetchTitleSuggestions = useCallback(async () => {
    setSuggestingTitle(true)
    setSuggestError(null)
    setTitleSuggestions([])
    try {
      const params = new URLSearchParams()
      if (state.exam_id) params.set('exam_id', String(state.exam_id))
      if (state.testType) params.set('test_type', state.testType)
      if (state.exam_test_type_id) params.set('exam_test_type_id', String(state.exam_test_type_id))
      if (state.language) params.set('language', state.language)
      if (state.totalQuestions) params.set('total_questions', String(state.totalQuestions))
      if (state.difficultyLevel) params.set('difficulty_level', state.difficultyLevel)
      if (state.pyqOnly) params.set('pyq_only', 'true')
      state.selectedSyllabiIds.forEach(id => params.append('syllabus_ids[]', String(id)))

      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const res = await fetch(`/admin/test_generator/suggest_title?${params.toString()}`, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(token ? { 'X-CSRF-Token': token } : {})
        }
      })
      const data = await res.json()
      if (!res.ok) {
        throw new Error(data.error || `Failed to fetch suggestions (Status ${res.status})`)
      }
      if (data.suggestions?.length > 0) {
        setTitleSuggestions(data.suggestions)
        setShowSuggestions(true)
      } else {
        setSuggestError('No suggestions generated. Try adjusting your configuration.')
      }
    } catch (e: any) {
      setSuggestError(e.message || 'Failed to fetch suggestions. Please try again.')
    } finally {
      setSuggestingTitle(false)
    }
  }, [state.exam_id, state.testType, state.exam_test_type_id, state.language, state.totalQuestions, state.difficultyLevel, state.pyqOnly, state.selectedSyllabiIds])

  const selectedCount = state.selectedQuestions.length
  const isPractice = state.testType === 'practice'
  const positiveMarks = state.markingScheme.positive_marks || 1
  const negativeMarks = state.markingScheme.negative_marks || 0.25

  const configuredRules = selectedExam?.marking_rules || []
  const activeBlueprintSections = (state.blueprintSections && state.blueprintSections.length > 0)
    ? state.blueprintSections.filter((bs: any) => bs._selected !== false)
    : ((selectedExam?.test_types?.find((tt: any) => tt.test_type_key === state.testType)?.sections) || [])
  const isUntimed = isPractice || state.noDuration || state.timerType === 'none'
  const isSectionalTimer = !isUntimed && (state.timerType ? state.timerType === 'sectional' : activeBlueprintSections.some((s: any) => (s.duration_minutes || 0) > 0))
  const isGlobalTimer = !isUntimed && !isSectionalTimer

  const sectionRules = state.markingScheme.section_marking_rules || {}
  const hasSectionalMarking = Object.keys(sectionRules).length > 0 || activeBlueprintSections.some((s: any) => s.marking_rule_id)

  const parseNum = (val: string): number => {
    if (!val || val.trim() === '') return 0
    const parsed = Number(val)
    return isNaN(parsed) ? 0 : parsed
  }

  const getSectionMarkingDetails = (sec: any) => {
    const overrideVal = sectionRules[sec.id]
    if (overrideVal === 'global') {
      return {
        title: state.markingScheme.rule_name || 'Global Scheme',
        pos: positiveMarks,
        wrongVal: state.markingScheme.negative_marking_enabled ? negativeMarks : 0,
        isGlobal: true
      }
    }
    const effectiveRuleId = overrideVal ?? sec.marking_rule_id
    const assignedRule = effectiveRuleId && effectiveRuleId !== 'custom' ? configuredRules.find((r: any) => r.id === effectiveRuleId) : null
    if (assignedRule) {
      const pos = assignedRule.scoring_params?.correct ?? assignedRule.scoring_params?.full_correct ?? positiveMarks
      const rawWrong = assignedRule.scoring_params?.wrong ?? assignedRule.scoring_params?.any_wrong ?? (assignedRule.scoring_strategy === 'no_penalty' ? 0 : -negativeMarks)
      const wrongVal = Math.abs(Number(rawWrong) || 0)
      return {
        title: assignedRule.name,
        pos: Number(pos) || 1,
        wrongVal,
        isGlobal: false
      }
    }
    return {
      title: state.markingScheme.rule_name || 'Standard Scheme',
      pos: positiveMarks,
      wrongVal: state.markingScheme.negative_marking_enabled ? negativeMarks : 0,
      isGlobal: true
    }
  }

  // Calculate max positive score and min possible penalty considering section-level overrides
  const { totalCalculatedMarks, maxPenaltyMarks } = React.useMemo(() => {
    let posTotal = 0
    let negTotal = 0

    if (state.selectedQuestions.length > 0) {
      const hasExplicitQuestionMarks = state.selectedQuestions.some(q => q.positive_marks !== undefined && q.positive_marks !== null)
      if (hasExplicitQuestionMarks) {
        posTotal = state.selectedQuestions.reduce((sum, q) => sum + (Number(q.positive_marks) || positiveMarks), 0)
        negTotal = isPractice ? 0 : state.selectedQuestions.reduce((sum, q) => sum + (Number(q.negative_marks) || 0), 0)
        return { totalCalculatedMarks: posTotal, maxPenaltyMarks: negTotal }
      }
    }

    if (hasSectionalMarking && activeBlueprintSections.length > 0) {
      activeBlueprintSections.forEach((sec: any) => {
        const ruleDetails = getSectionMarkingDetails(sec)
        const qCount = state.selectedQuestions.filter(q => q.section?.name === sec.name || q.section?.id === sec.id).length || (sec.total_questions || 0)
        posTotal += qCount * ruleDetails.pos
        if (!isPractice) {
          negTotal += qCount * ruleDetails.wrongVal
        }
      })
      if (posTotal > 0 || negTotal > 0) {
        return { totalCalculatedMarks: posTotal, maxPenaltyMarks: negTotal }
      }
    }

    const qCount = selectedCount > 0 ? selectedCount : state.totalQuestions
    posTotal = qCount * positiveMarks
    negTotal = (!isPractice && state.markingScheme.negative_marking_enabled) ? (qCount * negativeMarks) : 0
    return {
      totalCalculatedMarks: posTotal,
      maxPenaltyMarks: negTotal
    }
  }, [state.selectedQuestions, activeBlueprintSections, sectionRules, positiveMarks, negativeMarks, isPractice, hasSectionalMarking, selectedCount, state.totalQuestions, state.markingScheme.negative_marking_enabled])

  const isQuestionsMatch = selectedCount === state.totalQuestions
  const hasQuestions = selectedCount > 0

  return (
    <div className="space-y-6">
      {/* 1. Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
            {state.testId ? (
              <>
                <Save className="h-5 w-5 text-primary" />
                Final Review & Save Updates
              </>
            ) : (
              <>
                <Rocket className="h-5 w-5 text-primary" />
                Final Review & Test Generation
              </>
            )}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {state.testId
              ? 'Review updated test specifications, pricing, timing, marking rules, and question distribution before saving changes'
              : 'Review finalized test specifications, pricing, timing, marking rules, and question distribution before generation'}
          </p>
        </div>
      </div>

      {/* 2. Error Alert Banner */}
      {error && (
        <div className="p-3.5 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-xs font-medium flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* 3. Top Primary Configuration: Title, Language, Access Model & Credits */}
      <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-glass-xs space-y-4">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 border-b border-border/60 pb-2.5">
          <FileText className="h-4 w-4 text-primary" /> Test Identity & Access Model
        </span>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
          {/* Left Column: Title & Description combined */}
          <div className="md:col-span-8 space-y-4">
            {/* Test Title Input + Smart Suggestion */}
            <div className="space-y-1.5 relative" ref={suggestionsRef}>
              <div className="flex items-center justify-between">
                <Label htmlFor="review-title" className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  Test Title <span className="text-destructive">*</span>
                </Label>
                <button
                  id="title-suggest-btn"
                  type="button"
                  onClick={() => {
                    if (showSuggestions && titleSuggestions.length > 0) {
                      setShowSuggestions(false)
                    } else {
                      fetchTitleSuggestions()
                    }
                  }}
                  disabled={suggestingTitle || !state.exam_id || !state.testType}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors px-1.5 py-0.5 rounded-md hover:bg-primary/10"
                >
                  {suggestingTitle ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  <span>{suggestingTitle ? 'Generating…' : (showSuggestions && titleSuggestions.length > 0 ? 'Hide Suggestions' : 'Suggest Title')}</span>
                </button>
              </div>
              <div className="relative">
                <Input
                  id="review-title"
                  type="text"
                  value={state.title}
                  onChange={e => {
                    userClearedRef.current = e.target.value === ''
                    updateState({ title: e.target.value })
                  }}
                  className="h-9 text-xs font-bold bg-background rounded-xl border-border/80 focus:border-primary pr-8"
                  placeholder="e.g. SBI Clerk Full Mock 1"
                />
                {state.title && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      userClearedRef.current = true
                      updateState({ title: '' })
                    }}
                    title="Clear title"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded cursor-pointer z-10"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Error Message */}
              {suggestError && (
                <p className="text-[11px] text-destructive flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  <span>{suggestError}</span>
                </p>
              )}

              {/* Floating Smart Suggestion Popover */}
              {showSuggestions && titleSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1.5 z-30 bg-card border border-border rounded-xl shadow-glass-lg p-3 space-y-2 animate-in fade-in-50 slide-in-from-top-2 duration-150">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold text-foreground">Smart Title Suggestions</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-primary/30 text-primary font-semibold">
                        AI
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={fetchTitleSuggestions}
                        disabled={suggestingTitle}
                        title="Regenerate suggestions"
                        className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <RefreshCw className={`h-3 w-3 ${suggestingTitle ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSuggestions(false)}
                        title="Close"
                        className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {titleSuggestions.map((title, i) => {
                      const isSelected = state.title === title
                      return (
                        <button
                          key={i}
                          id={`title-suggestion-${i}`}
                          type="button"
                          onClick={() => {
                            userClearedRef.current = false
                            updateState({ title })
                            setShowSuggestions(false)
                          }}
                          className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-start justify-between gap-2.5 group ${
                            isSelected
                              ? 'bg-primary text-primary-foreground font-bold shadow-2xs'
                              : 'bg-muted/40 hover:bg-primary/10 text-foreground border border-border/50 hover:border-primary/30 font-medium'
                          }`}
                        >
                          <span className="break-words leading-snug flex-1 min-w-0">{title}</span>
                          {isSelected ? (
                            <Check className="h-4 w-4 shrink-0 mt-0.5" />
                          ) : (
                            <span className="text-[10px] text-muted-foreground group-hover:text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5">
                              Apply
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Test Description (Summary) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="review-description" className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                  Test Description (Subtitle / Summary)
                </Label>
                <span className="text-[10px] text-muted-foreground hidden sm:inline">
                  Displayed on student test cards
                </span>
              </div>
              <Input
                id="review-description"
                type="text"
                value={state.description || ''}
                onChange={e => updateState({ description: e.target.value })}
                className="h-9 text-xs bg-background rounded-xl border-border/80 focus:border-primary"
                placeholder="e.g. 20-minute timed sectional test covering English Language."
              />
            </div>
          </div>

          {/* Right Column: Language & Separate Pricing */}
          <div className="md:col-span-4 space-y-4">
            {/* Language Selector */}
            <div className="space-y-1.5">
              <Label htmlFor="review-language" className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                Language
              </Label>
              <Select
                value={state.language}
                onValueChange={val => updateState({ language: val })}
              >
                <SelectTrigger id="review-language" className="h-9 w-full text-xs font-semibold bg-background rounded-xl border-border/80 px-3 text-foreground focus:ring-2 focus:ring-primary/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                  <SelectItem value="en">English (EN)</SelectItem>
                  <SelectItem value="mr">Marathi (MR)</SelectItem>
                  <SelectItem value="hi">Hindi (HI)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Separate Access Pricing & Credit Cost */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="review-credit-cost" className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                  Credits Required
                </Label>
                {state.isFree ? (
                  <Badge variant="outline" className="text-[9px] font-bold h-4 px-1.5 bg-secondary/15 text-secondary border-secondary/30">
                    Free
                  </Badge>
                ) : (
                  <span className="text-[10px] font-semibold text-primary">Cost / attempt</span>
                )}
              </div>

              <div className="relative">
                <Input
                  id="review-credit-cost"
                  type={state.isFree ? 'text' : 'number'}
                  min={state.isFree ? undefined : "1"}
                  disabled={state.isFree}
                  value={state.isFree ? '0 (Free)' : (state.creditCost === 0 ? '' : state.creditCost)}
                  onChange={e => {
                    const val = parseNum(e.target.value)
                    updateState({
                      creditCost: val,
                      isFree: val === 0
                    })
                  }}
                  className={`h-9 text-xs font-bold pl-8 pr-3 rounded-xl border transition-all ${
                    state.isFree
                      ? 'bg-muted/40 border-border/70 text-muted-foreground cursor-not-allowed'
                      : 'bg-background border-border/80 text-foreground focus:border-primary'
                  }`}
                  placeholder={state.isFree ? 'Free (0)' : '5'}
                />
                <Coins className={`h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${
                  state.isFree ? 'text-muted-foreground opacity-50' : 'text-primary'
                }`} />
              </div>

              {/* Make Free Checkbox */}
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="review-is-free-checkbox"
                  checked={state.isFree}
                  onCheckedChange={(checked) => {
                    const isFree = !!checked
                    updateState({
                      isFree,
                      creditCost: isFree ? 0 : (state.creditCost > 0 ? state.creditCost : 5)
                    })
                  }}
                  className="h-4 w-4 rounded data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <Label
                  htmlFor="review-is-free-checkbox"
                  className="text-xs font-medium text-foreground cursor-pointer select-none"
                >
                  Make this test free
                </Label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Candidate Pre-Test Instructions & Examiner Notes Card */}
      <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-glass-xs space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-foreground">
              Candidate Instructions / Examiner Note
            </span>
          </div>
          <span className="text-[11px] text-muted-foreground">
            Displayed on candidate pre-test page & live exam room toolbar
          </span>
        </div>

        <div className="space-y-1.5">
          <textarea
            id="review-pre-test-instructions"
            rows={3}
            value={state.preTestInstructionsHtml || ''}
            onChange={e => updateState({ preTestInstructionsHtml: e.target.value })}
            placeholder="e.g. Read all questions carefully. Negative marking of 0.25 applies for incorrect answers."
            className="w-full p-3 rounded-xl border border-border/80 bg-background text-xs font-medium text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="text-[11px] text-muted-foreground">
            Pre-loaded from the selected blueprint. You can review or edit special candidate rules, instructions, or exam strategy notices before generating this test.
          </p>
        </div>
      </div>

      {/* 4. Timing & Marking Scheme Architecture Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Timing Architecture Card */}
        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-glass-xs space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary shrink-0" /> Timing Configuration
            </span>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {!isPractice && (
                <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted/60 p-1 border border-border/70 shadow-2xs w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => updateState({ timerType: 'global', noDuration: false, durationMinutes: state.durationMinutes || 180 })}
                    className={`px-2 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer text-center ${
                      isGlobalTimer
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Global
                  </button>
                  <button
                    type="button"
                    onClick={() => updateState({ timerType: 'sectional', noDuration: false, durationMinutes: state.durationMinutes || 180 })}
                    className={`px-2 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer text-center ${
                      isSectionalTimer
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Sectional
                  </button>
                  <button
                    type="button"
                    onClick={() => updateState({ timerType: 'none', noDuration: true, durationMinutes: null })}
                    className={`px-2 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer text-center ${
                      isUntimed
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Untimed
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="text-xs space-y-2">
            {isUntimed ? (
              <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 text-muted-foreground">
                <strong className="text-foreground">{isPractice ? 'Practice Mode:' : 'Untimed Test:'}</strong> No time limit. Students can solve and practice at their own pace without a countdown timer.
              </div>
            ) : isSectionalTimer ? (
              <div className="space-y-2">
                <p className="text-muted-foreground text-[11px]">
                  Sections run on independent countdown timers per exam pattern:
                </p>
                <div className="space-y-1.5">
                  {activeBlueprintSections
                    .filter((sec: any) => state.selectedQuestions.some(q => q.section?.name === sec.name || q.section?.id === sec.id))
                    .map((sec: any) => {
                      const allocatedCount = state.selectedQuestions.filter(q => q.section?.name === sec.name || q.section?.id === sec.id).length
                      const fallbackDuration = sec.duration_minutes || (allocatedCount > 0 && (state.durationMinutes || 0) > 0 ? Math.round((state.durationMinutes || 180) / (activeBlueprintSections.length || 1)) : 20)
                      const currentDuration = state.sectionDurations?.[sec.id] !== undefined && state.sectionDurations?.[sec.id] !== null
                        ? state.sectionDurations[sec.id]
                        : fallbackDuration

                      return (
                        <div key={sec.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 rounded-xl bg-muted/20 border border-border/60 gap-2">
                          <div className="flex items-center justify-between sm:justify-start gap-2 min-w-0">
                            <span className="font-semibold text-foreground text-xs leading-tight">{sec.name}</span>
                            <span className="text-[10px] text-muted-foreground font-medium shrink-0">({allocatedCount} Qs)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1.5 self-end sm:self-auto">
                            <span className="text-[11px] text-muted-foreground sm:hidden font-medium">Duration:</span>
                            <Input
                              type="number"
                              min={1}
                              max={499}
                              value={state.sectionDurations?.[sec.id] !== undefined ? (state.sectionDurations[sec.id] ?? '') : (sec.duration_minutes ?? 20)}
                              onChange={(e) => {
                                const raw = e.target.value
                                if (raw === '') {
                                  updateState({
                                    sectionDurations: {
                                      ...(state.sectionDurations || {}),
                                      [sec.id]: ''
                                    }
                                  })
                                  return
                                }
                                const num = parseInt(raw, 10)
                                if (!isNaN(num)) {
                                  if (num >= 500) {
                                    updateState({
                                      sectionDurations: {
                                        ...(state.sectionDurations || {}),
                                        [sec.id]: 499
                                      }
                                    })
                                  } else if (num < 0) {
                                    updateState({
                                      sectionDurations: {
                                        ...(state.sectionDurations || {}),
                                        [sec.id]: ''
                                      }
                                    })
                                  } else {
                                    updateState({
                                      sectionDurations: {
                                        ...(state.sectionDurations || {}),
                                        [sec.id]: num
                                      }
                                    })
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                const raw = e.target.value
                                const num = parseInt(raw, 10)
                                if (isNaN(num) || num <= 0) {
                                  updateState({
                                    sectionDurations: {
                                      ...(state.sectionDurations || {}),
                                      [sec.id]: fallbackDuration || 20
                                    }
                                  })
                                }
                              }}
                              className="w-16 h-7 text-xs font-mono text-primary text-center px-1 border-primary/30 focus:border-primary font-bold"
                            />
                            <span className="text-[11px] text-muted-foreground font-semibold">mins</span>
                          </div>
                        </div>
                      )
                    })}
                </div>
                {/* Live total duration sum for sectional timers */}
                <div className="flex justify-between items-center pt-2 border-t border-border/40 mt-2">
                  <span className="text-[11px] text-muted-foreground font-semibold">Total Test Duration</span>
                  <span className="text-xs font-extrabold text-primary">
                    {activeBlueprintSections
                      .filter((sec: any) => state.selectedQuestions.some(q => q.section?.name === sec.name || q.section?.id === sec.id))
                      .reduce((sum: number, sec: any) => {
                        const allocatedCount = state.selectedQuestions.filter(q => q.section?.name === sec.name || q.section?.id === sec.id).length
                        const fallbackDuration = sec.duration_minutes || (allocatedCount > 0 && (state.durationMinutes || 0) > 0 ? Math.round((state.durationMinutes || 180) / (activeBlueprintSections.length || 1)) : 20)
                        const dur = state.sectionDurations?.[sec.id] !== undefined && state.sectionDurations?.[sec.id] !== null
                          ? state.sectionDurations[sec.id]
                          : fallbackDuration
                        return sum + (Number(dur) || 0)
                      }, 0)} mins total
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl bg-muted/20 border border-border/60 gap-2.5">
                <div>
                  <div className="font-bold text-foreground text-xs">Global Test Duration</div>
                  <div className="text-[11px] text-muted-foreground">Single timer applies across the entire test</div>
                </div>
                <div className="flex items-center justify-end gap-1.5 self-end sm:self-auto">
                  <Input
                    type="number"
                    min={1}
                    max={499}
                    value={!state.durationMinutes ? '' : state.durationMinutes}
                    onChange={(e) => {
                      const raw = e.target.value
                      if (raw === '') {
                        updateState({ durationMinutes: 0 })
                        return
                      }
                      const num = parseInt(raw, 10)
                      if (!isNaN(num)) {
                        updateState({ durationMinutes: Math.min(499, Math.max(1, num)) })
                      }
                    }}
                    onBlur={() => {
                      if (!state.durationMinutes || state.durationMinutes <= 0) {
                        updateState({ durationMinutes: 60 })
                      }
                    }}
                    className="w-16 h-7 text-xs font-mono text-primary text-center px-1 border-primary/30 focus:border-primary font-bold"
                  />
                  <span className="text-[11px] text-muted-foreground font-semibold">mins</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Marking Scheme Architecture Card */}
        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-glass-xs space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2 border-b border-border/60 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Scale className="h-4 w-4 text-primary shrink-0" /> Marking Scheme Configuration
            </span>
            <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25 self-start sm:self-auto">
              {hasSectionalMarking ? 'Sectional Rules' : 'Global Scheme'}
            </Badge>
          </div>

          <div className="text-xs space-y-2">
            {hasSectionalMarking ? (
              <div className="space-y-2">
                <p className="text-muted-foreground text-[11px]">
                  Active sections with assigned marking scheme rules:
                </p>
                <div className="space-y-1.5">
                  {activeBlueprintSections.map((sec: any) => {
                    const ruleDetails = getSectionMarkingDetails(sec)
                    const cleanTitle = ruleDetails.title.replace(/\s*\(\+?[\d.]+\s*\/\s*-?[\d.]+\)/i, '').trim()
                    return (
                      <div key={sec.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 rounded-xl bg-muted/20 border border-border/60 gap-1.5">
                        <span className="font-semibold text-foreground text-xs">{sec.name}</span>
                        <div className="flex items-center justify-between sm:justify-end gap-2">
                          <span className="text-muted-foreground text-[11px] font-medium">{cleanTitle}</span>
                          <span className="font-mono text-[11px] font-bold">
                            <span className="text-primary">+{ruleDetails.pos}</span>
                            {ruleDetails.wrongVal > 0 ? (
                              <span className="text-destructive ml-1">-{ruleDetails.wrongVal}</span>
                            ) : (
                              <span className="text-muted-foreground font-normal ml-1">/ 0</span>
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl bg-muted/20 border border-border/60 gap-2">
                <div>
                  <div className="font-bold text-foreground text-xs">Uniform Marking Scheme</div>
                  <div className="text-[11px] text-muted-foreground">
                    {state.markingScheme.rule_name || 'Standard MCQ Scheme'}
                  </div>
                </div>
                <div className="flex sm:flex-col items-baseline sm:items-end justify-between sm:justify-start gap-1">
                  <div className="font-black text-primary text-sm">+{positiveMarks} / -{state.markingScheme.negative_marking_enabled ? negativeMarks : 0}</div>
                  <div className="text-[10px] text-muted-foreground">per question</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5. Exam Rules: Marked for Review, Calculator & Candidate Instructions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Marked for Review Rule Card */}
        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-glass-xs space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-border/60 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> Review Evaluation Rule
            </span>
            <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25 capitalize">
              {(state.markedForReviewRule || 'counts_if_answered').replace(/_/g, ' ')}
            </Badge>
          </div>

          <div className="p-3 rounded-xl bg-muted/20 border border-border/60 text-xs space-y-1.5 min-h-[90px]">
            <div className="font-bold text-foreground flex items-center gap-1.5">
              {state.markedForReviewRule === 'counts_if_answered' ? (
                <span>Counts if Answered (NTA Style)</span>
              ) : state.markedForReviewRule === 'never_counts' ? (
                <span>Never Counts (GATE Style)</span>
              ) : (
                <span>Always Evaluated</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {state.markedForReviewRule === 'counts_if_answered'
                ? 'Answered questions marked for review WILL be scored. Unanswered marked questions are ignored.'
                : state.markedForReviewRule === 'never_counts'
                ? 'Any question flagged for review at submission is excluded from scoring, even if answered.'
                : 'All marked questions will always be evaluated regardless of review status.'}
            </p>
          </div>
        </div>

        {/* On-Screen Calculator Card */}
        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-glass-xs space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-border/60 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Calculator className="h-4 w-4 text-primary shrink-0" /> On-Screen Calculator
            </span>
            <Badge
              variant="outline"
              className={`text-[10px] font-bold ${
                state.calculatorEnabled
                  ? 'bg-secondary/10 text-secondary border-secondary/25'
                  : 'bg-muted/30 text-muted-foreground border-border/60'
              }`}
            >
              {state.calculatorEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>

          <div className="p-3 rounded-xl bg-muted/20 border border-border/60 text-xs space-y-1.5 min-h-[90px] flex flex-col justify-between">
            <div>
              <div className="font-bold text-foreground flex items-center justify-between">
                <span>Candidate Calculator Tool</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.calculatorEnabled || false}
                    onChange={e => updateState({ calculatorEnabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-4 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
                {state.calculatorEnabled
                  ? 'Enables the interactive virtual calculator in the student test toolbar (GATE / Scientific format).'
                  : 'Disabled: Physical & digital calculators are prohibited during attempts.'}
              </p>
            </div>
          </div>
        </div>

        {/* Admin Note (Optional) Card */}
        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-glass-xs space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-border/60 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary shrink-0" /> Admin Note (Optional)
            </span>
            <Badge variant="outline" className={`text-[10px] font-bold ${
              state.preTestInstructionsHtml
                ? 'bg-secondary/10 text-secondary border-secondary/25'
                : 'bg-muted/30 text-muted-foreground border-border/60'
            }`}>
              {state.preTestInstructionsHtml ? 'Added' : 'None'}
            </Badge>
          </div>

          <div className="space-y-2 text-xs min-h-[90px] flex flex-col justify-between">
            <p className="text-muted-foreground text-[11px] leading-relaxed">
              Optional brief note or guidance for candidates. Test instructions, timings, and marking schemes are fully auto-generated.
            </p>

            <div className="p-2.5 bg-muted/20 border border-border/60 rounded-xl flex items-center justify-between gap-2">
              <span className="font-medium text-foreground truncate text-xs">
                {state.preTestInstructionsHtml || 'No admin note provided'}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setModalInstructions(state.preTestInstructionsHtml || '')
                  setIsEditingModal(true)
                  setIsInstructionsOpen(true)
                }}
                className="h-7 text-xs font-bold gap-1 rounded-lg border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 shrink-0 px-2.5"
              >
                <Edit3 className="h-3 w-3" /> Edit Note
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Admin Note Modal */}
      <Dialog open={isInstructionsOpen} onOpenChange={setIsInstructionsOpen}>
        <DialogContent className="max-w-lg flex flex-col p-6 rounded-2xl">
          <DialogHeader className="border-b border-border/60 pb-3">
            <DialogTitle className="text-base font-bold flex items-center gap-2 text-foreground">
              <FileText className="h-4 w-4 text-primary" />
              Admin Note (Optional)
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Add a short encouraging note or exam day reminder. Test rules, timings, and marking are auto-generated.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-2">
            <textarea
              value={modalInstructions}
              onChange={e => setModalInstructions(e.target.value)}
              rows={5}
              placeholder='e.g. "Good luck! Aim for the highest rank."'
              className="w-full text-xs p-3 bg-background rounded-xl border border-border/80 text-foreground leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
            />
            <p className="text-[11px] text-muted-foreground">
              Shown to candidates at the top of the pre-test instructions screen.
            </p>
          </div>

          <DialogFooter className="border-t border-border/60 pt-3 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsInstructionsOpen(false)}
              className="text-xs font-semibold"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                updateState({ preTestInstructionsHtml: modalInstructions.trim() })
                setIsInstructionsOpen(false)
              }}
              className="text-xs font-bold gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Check className="h-3.5 w-3.5" /> Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 5. Pre-Flight Checklist & Summary Metrics */}
      <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-glass-xs space-y-4">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 border-b border-border/60 pb-2.5">
          <ShieldCheck className="h-4 w-4 text-primary" /> Pre-Flight Generation Summary
        </span>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          {/* 1. Total Questions Card */}
          <div className="p-4 bg-muted/20 border border-border/70 rounded-2xl space-y-2 relative overflow-hidden flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Total Questions
              </span>
              <div className="h-6 w-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <FileText className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-black text-foreground">{selectedCount}</span>
                <span className="text-xs text-muted-foreground font-bold">/ {state.totalQuestions} Qs</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {isQuestionsMatch ? '✓ Matches target pool allocation' : '⚠ Question count mismatch'}
              </p>
            </div>
          </div>

          {/* 2. Evaluation Marks & Dynamic Score Range Card */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-2xl space-y-2 relative overflow-hidden flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                Evaluation Score Range
              </span>
              <div className="h-6 w-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Scale className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black text-primary">+{totalCalculatedMarks}</span>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase">Max</span>
                </div>
                {maxPenaltyMarks > 0 && (
                  <Badge variant="outline" className="text-[10px] font-bold bg-destructive/10 text-destructive border-destructive/25 py-0 px-1.5 h-5">
                    -{maxPenaltyMarks.toFixed(2).replace(/\.00$/, '')} Min Penalty
                  </Badge>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground font-mono mt-0.5 flex items-center gap-1">
                <span>Score Range:</span>
                <strong className={maxPenaltyMarks > 0 ? 'text-destructive font-bold' : 'text-foreground'}>
                  {maxPenaltyMarks > 0 ? `-${maxPenaltyMarks.toFixed(2).replace(/\.00$/, '')}` : '0'}
                </strong>
                <span>to</span>
                <strong className="text-primary font-bold">+{totalCalculatedMarks}</strong>
                <span>Marks</span>
              </div>
            </div>
          </div>

          {/* 3. Access & Pricing Card */}
          <div className="p-4 bg-muted/20 border border-border/70 rounded-2xl space-y-2 relative overflow-hidden flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Access Pricing
              </span>
              <Badge variant="outline" className={`text-[9px] font-extrabold py-0 px-1.5 h-4.5 ${
                state.isFree
                  ? 'bg-secondary/15 text-secondary border-secondary/30'
                  : 'bg-primary/15 text-primary border-primary/30'
              }`}>
                {state.isFree ? 'FREE TEST' : 'PAID ACCESS'}
              </Badge>
            </div>
            <div>
              <div className="flex items-center gap-2">
                {!state.isFree && (
                  <div className="h-6 w-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Coins className="h-3.5 w-3.5" />
                  </div>
                )}
                <div className="flex items-baseline gap-1">
                  <span className={`text-2xl font-black ${state.isFree ? 'text-secondary' : 'text-primary'}`}>
                    {state.isFree ? 'Free' : `${state.creditCost}`}
                  </span>
                  {!state.isFree && <span className="text-xs text-muted-foreground font-bold">Credits / attempt</span>}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {state.isFree ? 'Open to all enrolled candidates' : 'Deducted from candidate wallet balance'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Subject & Chapter Breakdown Card */}
      <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-glass-xs space-y-4">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 border-b border-border/60 pb-2.5">
          <BookOpen className="h-4 w-4 text-primary" /> Subject & Question Distribution Breakdown
        </span>

        <div className="space-y-3">
          {state.subjectWeightages
            .map(sw => {
              const subjectQuestions = state.selectedQuestions.filter(q => q.subject?.name === sw.name || q.subject?.id === sw.subject_id)
              const count = subjectQuestions.length
              const subMarks = subjectQuestions.reduce((sum, q) => sum + (Number(q.positive_marks) || positiveMarks), 0)
              return { sw, subjectQuestions, count, subMarks }
            })
            .filter(({ count }) => count > 0)
            .map(({ sw, subjectQuestions, count, subMarks }) => (
              <div key={sw.subject_id} className="p-3.5 bg-muted/15 rounded-xl border border-border/60 space-y-3">
                {/* Full-width Subject Name at top */}
                <div className="space-y-1.5 border-b border-border/50 pb-2">
                  <h4 className="font-bold text-foreground text-sm leading-snug break-words">
                    {sw.name}
                  </h4>
                  <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] font-medium py-0 px-2 rounded-full bg-muted/40 text-muted-foreground border-border/80">
                        {sw.chapters.length} chapters
                      </Badge>
                    </div>
                    <div className="font-extrabold text-primary text-xs">
                      {count} Questions • {subMarks} Marks
                    </div>
                  </div>
                </div>

                {sw.chapters.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pt-1">
                    {sw.chapters.map(ch => {
                      const chCount = subjectQuestions.filter(q => q.chapter?.name === ch.name || q.chapter?.id === ch.chapter_id).length
                      if (chCount === 0) return null
                      return (
                        <div
                          key={ch.chapter_id}
                          className="p-2.5 rounded-xl bg-card border border-border/80 flex flex-col justify-between gap-1.5 shadow-2xs"
                        >
                          <span className="text-xs font-semibold text-foreground leading-snug break-words">
                            {ch.name}
                          </span>
                          <div className="flex items-center justify-between pt-1 border-t border-border/40 text-[10px]">
                            <span className="text-muted-foreground font-medium">Chapter Pool</span>
                            <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25 px-1.5 py-0 h-4.5">
                              {chCount} Qs
                            </Badge>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
